class ReReHome {
  constructor(root) {
    this.root = root;
    this.rootUrl = root.dataset.rereRootUrl || "/";
    this.moneyFormat = root.dataset.rereMoneyFormat || "HK${{amount}}";
    this.currency = root.dataset.rereCurrency || "HKD";
    this.mobileQuery = window.matchMedia("(max-width: 749px)");
    this.tabs = Array.from(root.querySelectorAll("[data-rere-tab]"));
    this.panels = Array.from(root.querySelectorAll("[data-rere-panel]"));
    this.browseAll = root.querySelector("[data-rere-browse-all]");
    this.mobileMenu = root.querySelector(".rere-mobile-menu");
    this.panelStates = new Map();
    this.searchCache = new Map();
    this.searchTimer = null;
    this.searchController = null;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener("click", () => this.select(index));
      tab.addEventListener("keydown", (event) => this.handleTabKeydown(event, index));
    });

    this.prepareSearch();
    this.panels.forEach((panel) => this.preparePanel(panel));
    this.mobileQuery.addEventListener("change", () => this.resetPanels());

    root.querySelector("[data-rere-menu-close]")?.addEventListener("click", () => {
      this.mobileMenu?.removeAttribute("open");
      document.documentElement.style.overflow = "";
    });

    this.mobileMenu?.addEventListener("toggle", () => {
      document.documentElement.style.overflow = this.mobileMenu.open ? "hidden" : "";
    });
  }

  prepareSearch() {
    this.search = this.root.querySelector("[data-rere-search]");
    this.searchForm = this.search?.querySelector("[data-rere-search-form]");
    this.searchInput = this.search?.querySelector("[data-rere-search-input]");
    this.searchClear = this.search?.querySelector("[data-rere-search-clear]");
    this.searchResults = this.search?.querySelector("[data-rere-search-results]");
    this.searchStatus = this.search?.querySelector("[data-rere-search-status]");
    if (
      !this.search ||
      !this.searchForm ||
      !this.searchInput ||
      !this.searchClear ||
      !this.searchResults ||
      !this.searchStatus
    ) {
      return;
    }

    this.searchInput.addEventListener("input", () => this.scheduleSearch());
    this.searchInput.addEventListener("keydown", (event) => this.handleSearchInputKeydown(event));
    this.searchInput.addEventListener("focus", () => {
      if (this.searchResults.childElementCount > 0) this.openSearchResults();
    });
    this.searchClear.addEventListener("click", () => {
      this.searchInput.value = "";
      this.searchClear.hidden = true;
      this.closeSearchResults();
      this.searchInput.focus();
    });
    this.searchForm.addEventListener("submit", (event) => {
      if (!this.searchInput.value.trim()) event.preventDefault();
    });
    this.searchResults.addEventListener("keydown", (event) => this.handleSearchResultsKeydown(event));
    document.addEventListener("pointerdown", (event) => {
      if (!this.search.contains(event.target)) this.closeSearchResults();
    });
    this.search.dataset.rereSearchReady = "";
  }

  scheduleSearch() {
    const query = this.searchInput.value.trim();
    this.searchClear.hidden = query.length === 0;
    window.clearTimeout(this.searchTimer);
    this.searchController?.abort();

    if (query.length < 2) {
      this.closeSearchResults();
      this.searchStatus.textContent = "";
      return;
    }

    this.searchStatus.textContent = "正在搜尋商品";
    this.search.classList.add("is-loading");
    this.searchTimer = window.setTimeout(() => this.requestSearchSuggestions(query), 250);
  }

  async requestSearchSuggestions(query) {
    const normalizedQuery = query.toLocaleLowerCase();
    const cached = this.searchCache.get(normalizedQuery);
    if (cached) {
      this.renderSearchSuggestions(cached, query);
      return;
    }

    const controller = new AbortController();
    this.searchController = controller;
    const searchUrl = new URL(
      `${this.rootUrl.replace(/\/?$/, "/")}search`,
      window.location.origin,
    );
    searchUrl.searchParams.set("q", query);
    searchUrl.searchParams.set("type", "product");
    searchUrl.searchParams.set("options[prefix]", "last");
    searchUrl.searchParams.set("section_id", "rere-search-suggestions");

    try {
      const response = await fetch(searchUrl, {
        headers: { Accept: "text/html" },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, "text/html");
      const responseContent = parsed.querySelector("[data-rere-search-response]");
      if (!responseContent) throw new Error("Search response is missing");
      const markup = responseContent.outerHTML;
      this.searchCache.set(normalizedQuery, markup);
      this.renderSearchSuggestions(markup, query);
    } catch (error) {
      if (error.name === "AbortError") return;
      this.closeSearchResults();
      this.searchStatus.textContent = "暫時未能載入搜尋建議";
    } finally {
      this.search.classList.remove("is-loading");
      if (this.searchController === controller) this.searchController = null;
    }
  }

  renderSearchSuggestions(markup, query) {
    if (this.searchInput.value.trim().toLocaleLowerCase() !== query.toLocaleLowerCase()) return;
    this.searchResults.innerHTML = markup;
    this.searchStatus.textContent = this.searchResults.querySelector(".rere-search-suggestion")
      ? "搜尋建議已載入"
      : "未找到相關商品";
    this.openSearchResults();
    this.search.classList.remove("is-loading");
  }

  openSearchResults() {
    this.searchResults.hidden = false;
    this.searchInput.setAttribute("aria-expanded", "true");
  }

  closeSearchResults() {
    if (!this.searchResults) return;
    this.searchResults.hidden = true;
    this.searchInput?.setAttribute("aria-expanded", "false");
  }

  searchResultLinks() {
    return Array.from(this.searchResults.querySelectorAll("a"));
  }

  handleSearchInputKeydown(event) {
    if (event.key === "Escape") {
      this.closeSearchResults();
      return;
    }
    if (event.key !== "ArrowDown") return;
    const firstResult = this.searchResultLinks()[0];
    if (!firstResult || this.searchResults.hidden) return;
    event.preventDefault();
    firstResult.focus();
  }

  handleSearchResultsKeydown(event) {
    const links = this.searchResultLinks();
    const index = links.indexOf(document.activeElement);
    if (event.key === "Escape") {
      event.preventDefault();
      this.searchInput.focus();
      this.closeSearchResults();
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const direction = event.key === "ArrowDown" ? 1 : -1;
    const nextIndex = (index + direction + links.length) % links.length;
    links[nextIndex]?.focus();
  }

  preparePanel(panel) {
    const grid = panel.querySelector("[data-rere-product-grid]");
    const previous = panel.querySelector("[data-rere-products-previous]");
    const next = panel.querySelector("[data-rere-products-next]");
    const status = panel.querySelector("[data-rere-products-status]");
    if (!grid || !previous || !next || !status) return;

    const state = {
      grid,
      previous,
      next,
      status,
      page: 1,
      total: Number(panel.dataset.rereProductCount) || 0,
      handle: panel.dataset.rereCollectionHandle || "",
      initialCards: Array.from(grid.querySelectorAll("[data-rere-product-card]")).map((card) =>
        card.cloneNode(true),
      ),
      cache: new Map(),
      requests: new Map(),
      loadingKey: null,
      preloadTimer: null,
      preloadedImages: new Map(),
    };

    this.panelStates.set(panel, state);
    previous.addEventListener("click", () => this.changeProductPage(panel, -1));
    next.addEventListener("click", () => this.changeProductPage(panel, 1));
    this.renderInitialPage(panel);
  }

  pageSize() {
    return this.mobileQuery.matches ? 4 : 12;
  }

  totalPages(state) {
    return Math.max(1, Math.ceil(state.total / this.pageSize()));
  }

  canPreload() {
    const connection =
      navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) return true;
    return !connection.saveData && connection.effectiveType !== "slow-2g" && connection.effectiveType !== "2g";
  }

  cacheKey(pageSize, page) {
    return `${pageSize}:${page}`;
  }

  cancelScheduledPreload(state) {
    if (state.preloadTimer === null) return;
    window.clearTimeout(state.preloadTimer);
    state.preloadTimer = null;
  }

  cancelPanelWork(state) {
    this.cancelScheduledPreload(state);
    state.requests.forEach(({ controller }) => controller.abort());
    state.requests.clear();
    state.preloadedImages.forEach((images) => {
      images.forEach((image) => {
        image.src = "";
      });
    });
    state.preloadedImages.clear();
    state.loadingKey = null;
  }

  requestProductPage(state, pageSize, page) {
    const key = this.cacheKey(pageSize, page);
    const cached = state.cache.get(key);
    if (cached) return Promise.resolve(cached);

    const existing = state.requests.get(key);
    if (existing) return existing.promise;

    const controller = new AbortController();
    const collectionUrl = new URL(
      `collections/${encodeURIComponent(state.handle)}`,
      new URL(this.rootUrl, window.location.origin),
    );
    collectionUrl.searchParams.set("page", String(page));
    collectionUrl.searchParams.set("section_id", `rere-home-products-${pageSize}`);

    const promise = fetch(collectionUrl, {
      headers: { Accept: "text/html" },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, "text/html");
        const cards = Array.from(parsed.querySelectorAll("[data-rere-product-card]"), (card) =>
          card.outerHTML,
        );
        state.cache.set(key, cards);
        return cards;
      })
      .finally(() => {
        if (state.requests.get(key)?.promise === promise) state.requests.delete(key);
      });

    state.requests.set(key, { controller, promise });
    return promise;
  }

  preloadProductImages(state, key, cards) {
    const template = document.createElement("template");
    template.innerHTML = cards.join("");
    const images = Array.from(template.content.querySelectorAll("img"))
      .map((image) => image.getAttribute("src"))
      .filter(Boolean)
      .map((source) => {
        const image = new Image();
        image.decoding = "async";
        image.src = this.resizeImageUrl(source, 700);
        return image;
      });
    state.preloadedImages.clear();
    state.preloadedImages.set(key, images);
  }

  scheduleNextPagePreload(panel) {
    const state = this.panelStates.get(panel);
    if (!state || panel.hidden || !this.canPreload()) return;

    this.cancelScheduledPreload(state);
    const pageSize = this.pageSize();
    const nextPage = state.page + 1;
    if (nextPage > this.totalPages(state)) return;
    const key = this.cacheKey(pageSize, nextPage);

    state.preloadTimer = window.setTimeout(() => {
      state.preloadTimer = null;
      if (panel.hidden || nextPage !== state.page + 1) return;
      this.requestProductPage(state, pageSize, nextPage)
        .then((cards) => this.preloadProductImages(state, key, cards))
        .catch(() => {});
    }, 350);
  }

  renderInitialPage(panel) {
    const state = this.panelStates.get(panel);
    if (!state) return;

    this.cancelPanelWork(state);
    state.page = 1;
    state.grid.replaceChildren(
      ...state.initialCards.slice(0, this.pageSize()).map((card) => card.cloneNode(true)),
    );
    panel.removeAttribute("aria-busy");
    this.updateProductControls(state);
    this.scheduleNextPagePreload(panel);
  }

  resetPanels() {
    this.panels.forEach((panel) => this.renderInitialPage(panel));
  }

  async changeProductPage(panel, direction) {
    const state = this.panelStates.get(panel);
    if (!state || state.loadingKey) return;

    this.cancelScheduledPreload(state);
    const targetPage = state.page + direction;
    if (targetPage < 1 || targetPage > this.totalPages(state)) return;
    if (targetPage === 1) {
      this.renderInitialPage(panel);
      return;
    }

    const pageSize = this.pageSize();
    const key = this.cacheKey(pageSize, targetPage);
    let cards = state.cache.get(key);

    if (!cards) {
      state.loadingKey = key;
      panel.setAttribute("aria-busy", "true");
      this.setProductControlsLoading(state, true);

      try {
        cards = await this.requestProductPage(state, pageSize, targetPage);
      } catch (error) {
        if (error.name !== "AbortError") {
          state.status.textContent = "暫時未能載入，請重試";
        }
        return;
      } finally {
        state.loadingKey = null;
        panel.removeAttribute("aria-busy");
        this.setProductControlsLoading(state, false);
      }
    }

    if (!cards) return;
    state.grid.replaceChildren(...cards.map((markup) => this.productCardFromMarkup(markup)));
    state.page = targetPage;
    this.updateProductControls(state);
    this.scheduleNextPagePreload(panel);
  }

  setProductControlsLoading(state, loading) {
    state.previous.disabled = loading || state.page === 1;
    state.next.disabled = loading || state.page === this.totalPages(state);
    state.previous.classList.toggle("is-loading", loading);
    state.next.classList.toggle("is-loading", loading);
    if (loading) state.status.textContent = "載入中";
  }

  updateProductControls(state) {
    const pages = this.totalPages(state);
    state.previous.disabled = state.page <= 1;
    state.next.disabled = state.page >= pages;
    state.status.textContent = `第 ${state.page} 批`;
  }

  productCardFromMarkup(markup) {
    const template = document.createElement("template");
    template.innerHTML = markup.trim();
    return template.content.firstElementChild;
  }

  createProductCard(product) {
    const article = document.createElement("article");
    article.className = "rere-product-card";
    article.dataset.rereProductCard = "";

    const link = document.createElement("a");
    link.className = "rere-product-card__image";
    link.href = this.productUrl(product);

    const imageSource = this.productImage(product);
    if (imageSource) {
      const image = document.createElement("img");
      image.src = this.resizeImageUrl(imageSource, 700);
      image.alt = product.title || "";
      image.loading = "lazy";
      image.width = 700;
      image.height = 700;
      link.append(image);
    } else {
      link.classList.add("rere-product-card__image--empty");
    }

    const info = document.createElement("div");
    info.className = "rere-product-card__info";

    const title = document.createElement("a");
    title.className = "rere-product-card__title";
    title.href = link.href;
    title.textContent = product.title || "";

    const price = document.createElement("div");
    price.className = "rere-product-card__price";
    const currentPrice = this.productPrice(product);
    const priceText = document.createElement("span");
    priceText.textContent = this.formatMoney(currentPrice);
    price.append(priceText);

    const compareAtPrice = this.productCompareAtPrice(product);
    if (compareAtPrice > currentPrice) {
      const compareText = document.createElement("s");
      compareText.textContent = this.formatMoney(compareAtPrice);
      price.append(compareText);
    }

    info.append(title, price);
    article.append(link, info);
    return article;
  }

  productUrl(product) {
    if (product.url) return product.url;
    return new URL(
      `products/${encodeURIComponent(product.handle || "")}`,
      new URL(this.rootUrl, window.location.origin),
    ).pathname;
  }

  productImage(product) {
    const featured = product.featured_image;
    if (typeof featured === "string") return featured;
    if (featured?.src) return featured.src;
    const firstImage = product.images?.[0];
    if (typeof firstImage === "string") return firstImage;
    return firstImage?.src || "";
  }

  resizeImageUrl(source, width) {
    try {
      const url = new URL(source, window.location.origin);
      if (url.hostname.endsWith("cdn.shopify.com")) url.searchParams.set("width", String(width));
      return url.href;
    } catch {
      return source;
    }
  }

  productPrice(product) {
    const directPrice = this.toMinorUnits(product.price);
    if (directPrice !== null) return directPrice;
    const prices = (product.variants || [])
      .map((variant) => this.toMinorUnits(variant.price))
      .filter((price) => price !== null);
    return prices.length ? Math.min(...prices) : 0;
  }

  productCompareAtPrice(product) {
    const directPrice = this.toMinorUnits(product.compare_at_price);
    if (directPrice !== null) return directPrice;
    const prices = (product.variants || [])
      .map((variant) => this.toMinorUnits(variant.compare_at_price))
      .filter((price) => price !== null);
    return prices.length ? Math.min(...prices) : 0;
  }

  toMinorUnits(value) {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "number") return Number.isInteger(value) ? value : Math.round(value * 100);
    const normalized = String(value).trim().replace(/,/g, "");
    if (!normalized) return null;
    const number = Number(normalized);
    if (!Number.isFinite(number)) return null;
    return normalized.includes(".") ? Math.round(number * 100) : Math.round(number);
  }

  formatMoney(minorUnits) {
    const amount = (minorUnits / 100).toLocaleString(document.documentElement.lang || "zh-HK", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return this.moneyFormat.replace(/\{\{\s*amount(?:_[a-z_]+)?\s*\}\}/i, amount);
  }

  select(index) {
    const nextIndex = (index + this.tabs.length) % this.tabs.length;

    this.tabs.forEach((tab, tabIndex) => {
      const selected = tabIndex === nextIndex;
      tab.classList.toggle("is-active", selected);
      tab.setAttribute("aria-selected", String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });

    this.panels.forEach((panel, panelIndex) => {
      const selected = panelIndex === nextIndex;
      panel.classList.toggle("is-active", selected);
      panel.hidden = !selected;
      if (selected) {
        this.renderInitialPage(panel);
      } else {
        const state = this.panelStates.get(panel);
        if (state) this.cancelPanelWork(state);
      }
    });

    this.updateBrowseAll(nextIndex);
    this.tabs[nextIndex]?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  }

  updateBrowseAll(index) {
    const panel = this.panels[index];
    if (!this.browseAll || !panel) return;

    const collectionUrl = panel.dataset.rereCollectionUrl;
    const categoryLabel = panel.dataset.rereCategoryLabel;
    const prefix = this.browseAll.dataset.rereBrowsePrefix || "瀏覽全部";
    if (collectionUrl) this.browseAll.href = collectionUrl;
    if (categoryLabel) this.browseAll.textContent = `${prefix}${categoryLabel}`;
  }

  handleTabKeydown(event, index) {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const direction = event.key === "ArrowRight" ? 1 : -1;
    const nextIndex = (index + direction + this.tabs.length) % this.tabs.length;
    this.select(nextIndex);
    this.tabs[nextIndex]?.focus();
  }
}

document.querySelectorAll("[data-rere-home]").forEach((root) => new ReReHome(root));
